import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Clock, Building, Globe, Users, Award, 
  GraduationCap, Rocket, ArrowLeft, Star 
} from "lucide-react";

const timelineEvents = [
  {
    year: "2013",
    quarter: "March",
    title: "US Branch Founded",
    description: "Management to Learn Ltd. opens its first US branch in Houston, Texas, to support international students pursuing education in America.",
    icon: Building,
    type: "milestone",
    highlights: ["Houston, Texas HQ", "First US office", "Scholarship programs initiated"],
  },
  {
    year: "2014",
    quarter: "Q2",
    title: "MRMP Partnership",
    description: "Established partnership with Ministry of Mineral Resources and Petroleum of Angola to provide scholarships for Angolan students.",
    icon: Award,
    type: "partnership",
    highlights: ["Government partnership", "Scholarship funding", "Angolan students"],
  },
  {
    year: "2015",
    quarter: "Q1",
    title: "Academic Services Expansion",
    description: "Launched comprehensive student support services including housing, documentation guidance, and health insurance assistance.",
    icon: GraduationCap,
    type: "service",
    highlights: ["Housing support", "F1 visa guidance", "Health insurance"],
  },
  {
    year: "2016",
    quarter: "Q3",
    title: "Language Program Launch",
    description: "Introduced language courses and American host family accommodation programs to help students immerse in US culture.",
    icon: Users,
    type: "program",
    highlights: ["Language courses", "Host families", "Cultural immersion"],
  },
  {
    year: "2017",
    quarter: "Q2",
    title: "European Expansion",
    description: "Extended operations to European markets including France, Portugal, Poland, Germany, and the UK.",
    icon: Globe,
    type: "expansion",
    highlights: ["5 European countries", "Global network", "Cross-border programs"],
  },
  {
    year: "2018",
    quarter: "Q4",
    title: "100+ Cities Milestone",
    description: "Reached presence in over 100 cities worldwide, managing student experiences across three continents.",
    icon: Star,
    type: "milestone",
    highlights: ["100+ cities", "3 continents", "Growing alumni network"],
  },
  {
    year: "2020",
    quarter: "Q1",
    title: "Digital Transformation",
    description: "Adapted to remote support and digital services during global challenges, maintaining student support continuity.",
    icon: Rocket,
    type: "innovation",
    highlights: ["Digital services", "Remote support", "Online coordination"],
  },
  {
    year: "2023",
    quarter: "Q2",
    title: "10th Anniversary",
    description: "Celebrated a decade of transforming lives through international education, with thousands of successful alumni.",
    icon: Award,
    type: "milestone",
    highlights: ["10 years", "10,000+ alumni", "Global impact"],
  },
  {
    year: "2025",
    quarter: "Present",
    title: "New Digital Era",
    description: "Launched modern digital platform with AI-powered assistance, enhanced user experience, and expanded global services.",
    icon: Rocket,
    type: "innovation",
    highlights: ["AI integration", "Modern platform", "Enhanced services"],
  },
];

const HeritageTimelinePage = () => {
  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="py-16 bg-surface-solid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <Link
            to="/heritage"
            className="inline-flex items-center gap-2 text-secondary hover:underline mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Heritage
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <Clock className="w-6 h-6 text-secondary" />
              <span className="text-secondary text-sm font-medium tracking-[0.2em] uppercase">
                Brand Timeline
              </span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Our Journey Through Time
            </h1>
            <p className="text-xl text-white/60">
              From our founding in 2013 to becoming a global education network, 
              explore the key milestones that shaped M2L.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-[#060B19]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-secondary via-primary to-primary" />

            {/* Events */}
            <div className="space-y-12">
              {timelineEvents.map((event, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className={`relative flex items-start gap-8 ${
                    idx % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                >
                  {/* Dot */}
                  <div className="absolute left-8 md:left-1/2 transform -translate-x-1/2 z-10">
                    <div 
                      className="w-4 h-4 rounded-full border-4 border-[#060B19]"
                      style={{ 
                        backgroundColor: event.type === 'milestone' ? '#E2C792' : '#00E5FF' 
                      }}
                    />
                  </div>

                  {/* Content */}
                  <div className={`ml-20 md:ml-0 md:w-1/2 ${idx % 2 === 0 ? "md:pr-16" : "md:pl-16"}`}>
                    <div className="glass-card rounded-2xl p-6 hover:bg-white/5 transition-all">
                      {/* Year Badge */}
                      <div className="flex items-center gap-3 mb-4">
                        <span 
                          className="px-3 py-1 rounded-full text-xs font-bold"
                          style={{
                            backgroundColor: event.type === 'milestone' ? 'rgba(226, 199, 146, 0.2)' : 'rgba(0, 229, 255, 0.2)',
                            color: event.type === 'milestone' ? '#E2C792' : '#00E5FF',
                          }}
                        >
                          {event.year}
                        </span>
                        <span className="text-white/40 text-sm">{event.quarter}</span>
                      </div>

                      {/* Icon & Title */}
                      <div className="flex items-start gap-4 mb-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{
                            backgroundColor: event.type === 'milestone' ? 'rgba(226, 199, 146, 0.1)' : 'rgba(0, 229, 255, 0.1)',
                            borderColor: event.type === 'milestone' ? 'rgba(226, 199, 146, 0.2)' : 'rgba(0, 229, 255, 0.2)',
                            borderWidth: '1px',
                          }}
                        >
                          <event.icon 
                            className="w-5 h-5" 
                            style={{ color: event.type === 'milestone' ? '#E2C792' : '#00E5FF' }}
                          />
                        </div>
                        <h3 className="font-heading text-xl font-bold text-white">
                          {event.title}
                        </h3>
                      </div>

                      {/* Description */}
                      <p className="text-white/60 leading-relaxed mb-4">
                        {event.description}
                      </p>

                      {/* Highlights */}
                      <div className="flex flex-wrap gap-2">
                        {event.highlights.map((highlight, hIdx) => (
                          <span
                            key={hIdx}
                            className="px-3 py-1 text-xs rounded-full bg-white/5 border border-white/10 text-white/60"
                          >
                            {highlight}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-surface-solid">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Continue Exploring Our Legacy
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/heritage/archive"
              className="inline-flex items-center gap-2 px-6 py-3 bg-secondary text-[#060B19] rounded-lg font-semibold hover:bg-secondary/90 transition-all"
            >
              Browse Photo Archive
            </Link>
            <Link
              to="/heritage"
              className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white rounded-lg font-medium hover:bg-white/10 transition-all"
            >
              Back to Heritage
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HeritageTimelinePage;
